import React, { useEffect, useState, useCallback, useRef } from "react";
import Layout from "src/layout";
import Container from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import {
    getPaginatedCategories,
    getCategoryMegamenuProducts,
    saveCategoryMegamenuProducts,
    revalidateMegamenuCache
} from "../../lib/api/categoryApi";
import { getPaginatedProducts } from "../../lib/api/productsApi";
import {
    RiRefreshLine,
    RiArrowUpLine,
    RiArrowDownLine,
    RiDeleteBin6Line,
    RiAddLine,
    RiCheckLine,
    RiSaveLine,
    RiSearchLine,
    RiStackLine,
    RiStore2Line
} from "react-icons/ri";
import { toast } from "react-toastify";

const MegamenuProducts = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loadingCategories, setLoadingCategories] = useState(false);

    // Selected products for the active category
    const [assignedProducts, setAssignedProducts] = useState([]);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [saving, setSaving] = useState(false);
    const [revalidating, setRevalidating] = useState(false);

    // Search catalog
    const [searchQuery, setSearchQuery] = useState("");
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [catalogPage, setCatalogPage] = useState(1);
    const [hasMoreCatalog, setHasMoreCatalog] = useState(false);

    const searchTimerRef = useRef(null);

    // Helper to get image
    const getProductImg = (val) => {
        if (!val) return "/placeholder.png";
        try {
            if (Array.isArray(val)) return val[0]?.imgUrl || val[0] || "/placeholder.png";
            if (typeof val === "string") {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed[0]?.imgUrl || parsed[0] || "/placeholder.png";
                if (typeof parsed === "object" && parsed?.imgUrl) return parsed.imgUrl;
                if (val.startsWith("http") || val.startsWith("/")) return val;
            }
            return "/placeholder.png";
        } catch {
            return typeof val === "string" && (val.startsWith("http") || val.startsWith("/")) ? val : "/placeholder.png";
        }
    };

    // 1. Fetch all categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoadingCategories(true);
                const res = await getPaginatedCategories({ page: 1, limit: 100 });
                const list = res.data || (Array.isArray(res) ? res : []);
                setCategories(list);
                if (list.length > 0) {
                    setSelectedCategory(list[0]);
                }
            } catch (err) {
                console.error("Error fetching categories:", err);
                toast.error("Failed to fetch categories");
            } finally {
                setLoadingCategories(false);
            }
        };

        fetchCategories();
    }, []);

    // 2. Fetch assigned products when selectedCategory changes
    useEffect(() => {
        if (!selectedCategory?.categoryID) return;

        const fetchAssigned = async () => {
            try {
                setLoadingAssigned(true);
                const res = await getCategoryMegamenuProducts(selectedCategory.categoryID);
                if (res.success && res.data) {
                    setAssignedProducts(res.data.products || []);
                } else {
                    setAssignedProducts([]);
                }
            } catch (err) {
                console.error("Error fetching category megamenu products:", err);
                setAssignedProducts([]);
            } finally {
                setLoadingAssigned(false);
            }
        };

        fetchAssigned();
    }, [selectedCategory]);

    // 3. Search catalog products
    const executeSearch = useCallback(async (query, page = 1, append = false) => {
        try {
            setLoadingSearch(true);
            const filters = {};
            if (query && query.trim()) {
                filters.search = query.trim();
            }

            const res = await getPaginatedProducts({ page, limit: 12, filters });
            const items = res.products || res.data || (Array.isArray(res) ? res : []);

            if (append) {
                setAvailableProducts(prev => [...prev, ...items]);
            } else {
                setAvailableProducts(items);
            }
            setHasMoreCatalog(items.length >= 12);
        } catch (err) {
            console.error("Error searching products:", err);
        } finally {
            setLoadingSearch(false);
        }
    }, []);

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setCatalogPage(1);
            executeSearch(searchQuery, 1, false);
        }, 300);

        return () => clearTimeout(searchTimerRef.current);
    }, [searchQuery, executeSearch]);

    // Add product to assigned list
    const handleAddProduct = (prod) => {
        if (assignedProducts.some(p => String(p.productID) === String(prod.productID))) {
            toast.info("Product already added to this category");
            return;
        }
        setAssignedProducts(prev => [...prev, prod]);
    };

    // Remove product from assigned list
    const handleRemoveProduct = (prodID) => {
        setAssignedProducts(prev => prev.filter(p => String(p.productID) !== String(prodID)));
    };

    // Move product up or down
    const handleMove = (index, direction) => {
        const next = [...assignedProducts];
        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= next.length) return;
        const temp = next[index];
        next[index] = next[targetIndex];
        next[targetIndex] = temp;
        setAssignedProducts(next);
    };

    // Save assigned products
    const handleSave = async () => {
        if (!selectedCategory?.categoryID) return;
        try {
            setSaving(true);
            const productIDs = assignedProducts.map(p => p.productID);
            const res = await saveCategoryMegamenuProducts(selectedCategory.categoryID, productIDs);
            if (res.success) {
                toast.success("Mega menu products saved and Redis cache revalidated!");
            } else {
                toast.error(res.error || res.message || "Failed to save");
            }
        } catch (err) {
            console.error("Save error:", err);
            toast.error("Internal server error while saving");
        } finally {
            setSaving(false);
        }
    };

    // Explicit Redis revalidation
    const handleRevalidateRedis = async () => {
        try {
            setRevalidating(true);
            const res = await revalidateMegamenuCache();
            if (res.success) {
                toast.success("Redis mega menu cache purged and revalidated with No TTL!");
            } else {
                toast.error(res.error || res.message || "Revalidation failed");
            }
        } catch (err) {
            console.error("Revalidation error:", err);
            toast.error("Error revalidating Redis cache");
        } finally {
            setRevalidating(false);
        }
    };

    return (
        <Layout active={"admin-category-megamenu"}>
            <div className="min-h-screen bg-slate-50 p-6">
                <Container>
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-gray-200 gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="p-2 bg-pink-100 text-[#fb4a6f] rounded-lg">
                                    <RiStackLine size={20} />
                                </span>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Category Mega Menu Carousel Products
                                </h1>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                Curate products shown in the Mega Menu carousel for each category. Stored in Redis with No TTL.
                            </p>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleRevalidateRedis}
                                variant="outline"
                                className="flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                                disabled={revalidating}
                            >
                                <RiRefreshLine className={revalidating ? "animate-spin" : ""} />
                                <span>Revalidate Redis Cache</span>
                            </Button>

                            <Button
                                onClick={handleSave}
                                className="flex items-center gap-2 bg-[#fb4a6f] hover:bg-[#e03a5e] text-white"
                                disabled={saving || !selectedCategory}
                            >
                                <RiSaveLine />
                                <span>{saving ? "Saving..." : "Save Products & Sync"}</span>
                            </Button>
                        </div>
                    </div>

                    {/* Category Selector Bar */}
                    <div className="bg-white p-4 rounded-xl shadow-xs border border-gray-150 my-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-700">Select Category:</span>
                            {loadingCategories ? (
                                <span className="text-sm text-gray-400">Loading categories...</span>
                            ) : (
                                <select
                                    value={selectedCategory?.categoryID || ""}
                                    onChange={(e) => {
                                        const cat = categories.find(c => String(c.categoryID) === e.target.value);
                                        if (cat) setSelectedCategory(cat);
                                    }}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-pink-500 min-w-[220px]"
                                >
                                    {categories.map((c) => (
                                        <option key={c.categoryID} value={c.categoryID}>
                                            {c.categoryName} (ID: {c.categoryID})
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            <span>Redis Cache Key: <strong>cache:categories:megamenu_brands</strong> (Permanent / No TTL)</span>
                        </div>
                    </div>

                    {/* Main Two-Column Workspace */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        
                        {/* LEFT COLUMN: Curated Carousel Products */}
                        <div className="lg:col-span-6 bg-white rounded-xl shadow-xs border border-gray-150 p-5 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-150">
                                    <div>
                                        <h2 className="font-bold text-gray-800 text-base flex items-center gap-2">
                                            <span>Active Carousel Products</span>
                                            <span className="bg-pink-100 text-[#fb4a6f] text-xs font-semibold px-2 py-0.5 rounded-full">
                                                {assignedProducts.length}
                                            </span>
                                        </h2>
                                        <p className="text-xs text-gray-400 mt-0.5">
                                            Display order in the mega menu for <strong>{selectedCategory?.categoryName || "Category"}</strong>
                                        </p>
                                    </div>

                                    {assignedProducts.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setAssignedProducts([])}
                                            className="text-xs text-red-500 hover:text-red-700 font-semibold"
                                        >
                                            Clear All
                                        </button>
                                    )}
                                </div>

                                {loadingAssigned ? (
                                    <div className="py-12 text-center text-sm text-gray-400 animate-pulse">
                                        Loading assigned products...
                                    </div>
                                ) : assignedProducts.length === 0 ? (
                                    <div className="py-12 px-4 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                        <RiStore2Line size={36} className="mx-auto text-gray-300 mb-2" />
                                        <p className="text-sm font-semibold text-gray-700">No Custom Products Selected</p>
                                        <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                                            The mega menu will automatically show the latest active products for this category. Search and add products from the right to manually curate this carousel.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-[580px] overflow-y-auto pr-1">
                                        {assignedProducts.map((prod, index) => {
                                            const img = getProductImg(prod.featuredImage);
                                            const price = Number(prod.salePrice ?? prod.regularPrice ?? 0);

                                            return (
                                                <div
                                                    key={prod.productID}
                                                    className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 hover:border-pink-200 bg-white shadow-2xs transition-all gap-3"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <span className="text-xs font-bold text-gray-400 w-5 text-center">
                                                            {index + 1}
                                                        </span>
                                                        <img
                                                            src={img}
                                                            alt={prod.name}
                                                            className="w-12 h-14 rounded object-cover bg-gray-100 shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                            <h3 className="text-xs font-semibold text-gray-900 truncate">
                                                                {prod.name}
                                                            </h3>
                                                            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                                                                <span className="font-semibold text-gray-800">₹{price.toLocaleString("en-IN")}</span>
                                                                {prod.brand && <span>• {prod.brand}</span>}
                                                                <span>• ID: {prod.productID}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMove(index, "up")}
                                                            disabled={index === 0}
                                                            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-600 transition"
                                                            title="Move Up"
                                                        >
                                                            <RiArrowUpLine size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMove(index, "down")}
                                                            disabled={index === assignedProducts.length - 1}
                                                            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-600 transition"
                                                            title="Move Down"
                                                        >
                                                            <RiArrowDownLine size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveProduct(prod.productID)}
                                                            className="p-1.5 rounded hover:bg-red-50 text-red-500 transition ml-1"
                                                            title="Remove"
                                                        >
                                                            <RiDeleteBin6Line size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="pt-4 border-t border-gray-100 mt-4 flex items-center justify-between">
                                <span className="text-xs text-gray-400">
                                    Remember to click Save to persist and revalidate Redis.
                                </span>
                                <Button
                                    onClick={handleSave}
                                    className="bg-black hover:bg-gray-800 text-white text-xs px-4 py-2"
                                    disabled={saving || !selectedCategory}
                                >
                                    {saving ? "Saving..." : "Save Order"}
                                </Button>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Search & Select Catalog Products */}
                        <div className="lg:col-span-6 bg-white rounded-xl shadow-xs border border-gray-150 p-5 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-150">
                                    <div>
                                        <h2 className="font-bold text-gray-800 text-base">Product Catalog</h2>
                                        <p className="text-xs text-gray-400 mt-0.5">Search and add products to the carousel</p>
                                    </div>
                                </div>

                                {/* Search Bar */}
                                <div className="relative mb-4">
                                    <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="Search by product name or ID..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    />
                                </div>

                                {/* Catalog List */}
                                {loadingSearch && availableProducts.length === 0 ? (
                                    <div className="py-12 text-center text-sm text-gray-400 animate-pulse">
                                        Searching products...
                                    </div>
                                ) : availableProducts.length === 0 ? (
                                    <div className="py-12 text-center text-sm text-gray-400">
                                        No products found.
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                                        {availableProducts.map((prod) => {
                                            const isAdded = assignedProducts.some(p => String(p.productID) === String(prod.productID));
                                            const img = getProductImg(prod.featuredImage);
                                            const price = Number(prod.salePrice ?? prod.regularPrice ?? 0);

                                            return (
                                                <div
                                                    key={prod.productID}
                                                    className={`flex items-center justify-between p-2.5 rounded-lg border transition-all gap-3 ${
                                                        isAdded ? "bg-gray-50 border-gray-200 opacity-70" : "bg-white border-gray-200 hover:border-pink-300"
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <img
                                                            src={img}
                                                            alt={prod.name}
                                                            className="w-12 h-14 rounded object-cover bg-gray-100 shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                            <h3 className="text-xs font-semibold text-gray-900 truncate">
                                                                {prod.name}
                                                            </h3>
                                                            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                                                                <span className="font-semibold text-gray-800">₹{price.toLocaleString("en-IN")}</span>
                                                                {prod.brand && <span>• {prod.brand}</span>}
                                                                <span>• ID: {prod.productID}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="shrink-0">
                                                        {isAdded ? (
                                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">
                                                                <RiCheckLine size={13} /> Added
                                                            </span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleAddProduct(prod)}
                                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#fb4a6f] hover:text-white border border-[#fb4a6f] hover:bg-[#fb4a6f] px-2.5 py-1 rounded-md transition-all shadow-2xs"
                                                            >
                                                                <RiAddLine size={13} /> Add
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {hasMoreCatalog && (
                                <div className="pt-3 border-t border-gray-100 mt-3 text-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const nextPage = catalogPage + 1;
                                            setCatalogPage(nextPage);
                                            executeSearch(searchQuery, nextPage, true);
                                        }}
                                        disabled={loadingSearch}
                                        className="text-xs font-semibold text-[#fb4a6f] hover:underline"
                                    >
                                        {loadingSearch ? "Loading more..." : "Load More Products ↓"}
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default MegamenuProducts;
